// Sentry browser SDK bootstrap — runs in the client bundle.
// Must stay in the project root so Next.js picks it up automatically.
//
// Architectural note (2026-05-25): switched from @sentry/nextjs → @sentry/react
// to drop the @sentry/node + 24 @opentelemetry/instrumentation-* server-side
// chain that was driving Cloud Build OOM at the NFT trace step. We use Sentry
// for client-side error tracking + replay only; server-side errors flow
// through Cloud Logging + the existing reportError() pipeline.
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NODE_ENV,
  tracesSampleRate: 0.1,
  // Replay is useful but has its own event quota — keep both rates low.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0.1,
  beforeSend(event) {
    if (event.user) {
      delete event.user.email;
      delete event.user.ip_address;
    }
    return event;
  },
});

// Bug Center — mirror uncaught client errors into our errorEvents collection.
// Late dynamic import avoids pulling Firestore into the Sentry-init module
// (instrumentation runs before our Firebase bootstrap finishes).
if (typeof window !== "undefined") {
  const forward = (err: unknown, kind: "error" | "unhandledrejection"): void => {
    void (async () => {
      try {
        const [{ reportError }, { getCurrentWebContext, detectEnvironment }] = await Promise.all([
          import("@eventify/shared/lib/errorReporter"),
          import("./src/lib/errorContext"),
        ]);
        reportError(err, {
          surface: "web",
          source: "global",
          ...getCurrentWebContext(),
          extra: { kind },
        }, { environment: detectEnvironment() });
      } catch {
        // never throw from a global handler
      }
    })();
  };
  window.addEventListener("error", (event) => {
    forward(event.error ?? new Error(event.message || "window.onerror"), "error");
  });
  window.addEventListener("unhandledrejection", (event) => {
    forward(event.reason ?? new Error("UnhandledRejection (no reason)"), "unhandledrejection");
  });
}
